import { Breakpoints } from '@angular/cdk/layout';
import { Component, Inject, Input, OnDestroy } from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import {BehaviorSubject, EMPTY, firstValueFrom, from, Observable} from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

import { ClassName } from '@sim/role/models/role';
import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableComponent,
  TableContextMenu,
  TableField,
  UiTableComponent,
} from '@ui/ui.module';

import { CardsApiService, CardType, filePath, SourceArcApiService, SwalService } from '@atmi/core';
import { CardD, CardMI, cardsOptions, CommonCard, SourceArchivial } from '@atmi/core/models';
import { CardF } from '@src/app/core/models/cards/card-f';
import { CardOA } from '@src/app/core/models/cards/card-oa';
import { CardS } from '@src/app/core/models/cards/card-s';
import { CardsSourceArcDialogComponent } from '../cards-source-arc-dialog/cards-source-arc-dialog.component';

type SourceArchivialConcrete = SourceArchivial & { id: string };

@Component({
  selector: 'app-arc-cards',
  styleUrls: ['./arc-cards.component.scss'],
  templateUrl: './arc-cards.component.html',
})
export class ArcCardsComponent
  extends BaseTableComponent<CommonCard>
  implements UiTableComponent<CommonCard>, OnDestroy {

  private readonly sourceArc$ = new BehaviorSubject<SourceArchivial | null>(null);
  @Input() set sourceArc(sourceArc: SourceArchivial | null) {
    if (sourceArc) {
      this.sourceArc$.next(sourceArc);
    }
  }
  get sourceArc() {
    return this.sourceArc$.value;
  }

  readonly fields: TableField<CommonCard>[] = [
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      header: this.translate.instant('Tipo scheda'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'TSK',
    },
    {
      header: this.translate.instant('Codice unico'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'codiceUnico',
    },
    {
      context: (x) => x.SGT.SGTT,
      header: this.translate.instant('Titolo soggetto'),
      key: 'SGT.SGTT',
    },
    {
      header: this.translate.instant('Fornitore'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'fornitore',
    },
    {
      header: this.translate.instant('Data creazione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'createdAt',
      template: 'date-time',
    },
  ];

  readonly pager$ = this.sourceArc$.pipe(
    filter((sourceArc): sourceArc is SourceArchivialConcrete => !!sourceArc),
    map((sourceArc) => ((page?: number, limit?: number) =>
      this.sourceArcApi.getCards(sourceArc.id, { page, limit })),
    ),
  );

  readonly contextMenus: Array<TableContextMenu<CommonCard>> = [];

  readonly cardOptions = cardsOptions;

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly cardsApi: CardsApiService,
    private readonly dialog: MatDialog,
    private readonly roleService: RoleService,
    private readonly sourceArcApi: SourceArcApiService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
  ) {
    super(translate, router, route, swalService);

    const hasCapabilityToUpdate = (['BeneOA', 'BeneF', 'BeneMI', 'BeneD', 'BeneS'] as ClassName[]).some(
      (className) => this.roleService.hasCapability(className, ['update'])
    );

    if (hasCapabilityToUpdate) {
      this.contextMenus.push({
        action: (item) => this.router.navigate([`cards/${item.TSK.toLowerCase()}/manager`, item.id]),
        label: this.translate.instant('Modifica'),
      });
    } else {
      this.contextMenus.push({
        action: (item) => this.router.navigate([`cards/${item.TSK.toLowerCase()}/manager`, item.id]),
        label: this.translate.instant('Visualizza'),
      });
    }

    const hasCapabilityToDelete = (['BeneOA', 'BeneF', 'BeneMI', 'BeneD', 'BeneS'] as ClassName[]).some(
      (className) => this.roleService.hasCapability(className, ['delete'])
    );

    if (hasCapabilityToDelete) {
      this.contextMenus.push({
        action: (item) => item.id
          ? this.deleteHandler(item.id, item, { text: 'Sei sicuro di voler procede?', title: 'Scollega bene' })
          : () => undefined,
        label: this.translate.instant('Scollega bene'),
      });
    }
  }

  ngOnDestroy() {
    this.sourceArc$.complete();
  }

  deleteConcreteHandler(_: string, card: CommonCard) {
    const cardToUpdate: CommonCard = {
      ...card,
      fontiArc: [...card.fontiArc.filter((fa) => fa.docArc !== this.sourceArc?.id)],
    };

    switch (card.TSK.toLowerCase()) {
      case 'oa':
        return this.cardsApi.putOA(cardToUpdate as CardOA);
      case 'd':
        return this.cardsApi.putD(cardToUpdate as CardD, 'd');
      case 'mi':
        return this.cardsApi.put(cardToUpdate as CardMI, 'mi');
      case 'f':
        return this.cardsApi.putF(cardToUpdate as CardF, 'f');
      case 's':
        return this.cardsApi.putS(cardToUpdate as CardS, 's');
      default:
        return this.cardsApi.putOA(cardToUpdate as CardOA);
    }
  }

  // tslint:disable-next-line:variable-name
  deleteSelectedConcreteHandler(_ids: string[]) {
    return from(Promise.all([]));
  }

  async bindCardHandler(type: CardType) {
    if (!this.sourceArc) {
      return;
    }

    const ref = this.dialog.open(CardsSourceArcDialogComponent, { data: { type, docArc: this.sourceArc.id }, width: '1000px' });
    const result = await firstValueFrom(ref.afterClosed().pipe(first()));

    if (result) {
      this.reload$.next(true);
    }
  }
}
